// ProjectBoard — the listing's Kanban view: DataBoard columns from the page's
// group-by field (`state` is the default — the lifecycle `status` stays the
// tab's business), one ProjectBoardCard per project, load-more pagination via
// CardsLoadMore. Render-only: the page owns the query, page accumulation and
// navigation; column counts are derived from the loaded cards (the API has no
// per-column totals). Only static enum fields every project carries may drive
// the columns — DataBoard drops unknown keys, so an uncovered value would hide
// cards.

import { useTranslation } from 'react-i18next';
import { CardsLoadMore, DataBoard, Icon, type DataBoardColumn } from '@/components/common';
import type { ProjectListItem } from '@/api/endpoints/projectsApi';
import ProjectBoardCard from './ProjectBoardCard';

export type ProjectBoardField = 'state' | 'priority';

// Column defs per groupable field, in rank order; the priority tones mirror
// the table's PRIORITY_META chips (urgent shares high's danger tier). The
// i18n key prefixes match the group bands the table draws.
const BOARD_COLUMNS: Record<ProjectBoardField, Omit<DataBoardColumn, 'label'>[]> = {
  state: [{ key: 'active', tone: 'success' }, { key: 'inactive' }],
  priority: [
    { key: 'urgent', tone: 'danger' },
    { key: 'high', tone: 'danger' },
    { key: 'medium', tone: 'warning' },
    { key: 'low', tone: 'info' },
  ],
};
const LABEL_PREFIX: Record<ProjectBoardField, string> = {
  state: 'projects_status_',
  priority: 'projects_priority_',
};

interface ProjectBoardProps {
  /** Defaults already prepended by the page on the Live tab. */
  items: ProjectListItem[];
  /** First fetch of an empty board — cards hidden, status shown. */
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  /** Next-page fetch in flight — the Load-more button spins. */
  loadingMore: boolean;
  onLoadMore: () => void;
  /** Archived tab — only the empty-state icon differs (table parity). */
  archivedTab: boolean;
  /** The field the columns split on — the page's group-by pick ('state' when
   *  grouping is off: a board always has a column field). */
  groupField: ProjectBoardField;
  onOpen: (project: ProjectListItem) => void;
}

const ProjectBoard = ({
  items,
  loading,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  archivedTab,
  groupField,
  onOpen,
}: ProjectBoardProps) => {
  const { t } = useTranslation();

  const columns: DataBoardColumn[] = BOARD_COLUMNS[groupField].map((col) => ({
    ...col,
    label: t(`${LABEL_PREFIX[groupField]}${col.key}`),
  }));

  return (
    <>
      <DataBoard
        columns={columns}
        items={items}
        itemKey={(p) => p.id}
        columnKey={(p) => p[groupField]}
        onItemClick={(p) => onOpen(p)}
        emptyColumnText={t('projects_board_column_empty')}
        loading={loading}
        error={error}
        loadingText={t('text_loading')}
        errorText={t('error_generic')}
        empty={
          <>
            <Icon name={archivedTab ? 'archive' : 'folder-kanban'} size={26} />
            {t('projects_empty')}
          </>
        }
        renderCard={(p) => <ProjectBoardCard project={p} />}
      />
      <CardsLoadMore
        hasMore={hasMore}
        loading={loadingMore}
        onLoadMore={onLoadMore}
        label={t('text_load_more')}
      />
    </>
  );
};

export default ProjectBoard;
